import { z } from 'zod';
import './schemas';

/**
 * Podpis elektroniczny umów (P3b, ADR-015) — podpis elektroniczny ZWYKŁY w rozumieniu eIDAS (art. 3 pkt 10):
 * link + oświadczenia + jednorazowy kod (SMS/e-mail) + ślad dowodowy, dokument końcowy zapieczętowany (PAdES).
 * Nie jest to podpis kwalifikowany — ten wymaga zewnętrznego dostawcy usług zaufania.
 */

export const ESIGN_STATUSES = {
  przygotowuje: 'Przygotowanie dokumentu',
  w_toku: 'W toku',
  finalizacja: 'Składanie dokumentu',
  zakonczony: 'Podpisano',
  odrzucony: 'Odmowa podpisu',
  anulowany: 'Anulowano',
  wygasly: 'Wygasło',
} as const;
export type EsignStatus = keyof typeof ESIGN_STATUSES;

export const ESIGN_SIGNER_STATUSES = {
  oczekuje: 'Oczekuje',
  podpisal: 'Podpisano',
  odmowil: 'Odmowa',
} as const;
export type EsignSignerStatus = keyof typeof ESIGN_SIGNER_STATUSES;

/**
 * QA P3: umowa wydana na papier ma zamrożoną treść dla podpisu odręcznego („dwa jednobrzmiące egzemplarze”, linie podpisu).
 * Wysyłka takiej treści do e-podpisu tylko świadomie; zwykle — ponowne wydanie do podpisu elektronicznego.
 */
export const ESIGN_PAPER_ISSUED_MESSAGE =
  'Umowa została wydana do podpisu papierowego — jej treść ma zdanie o dwóch egzemplarzach i linie podpisu odręcznego. Użyj „Wydaj ponownie do e-podpisu” (nowa wersja z treścią dla podpisu elektronicznego) albo potwierdź wysyłkę bez zmiany treści.';

export const ESIGN_ORDERS = {
  klient_pierwszy: 'Najpierw klient, potem firma',
  firma_pierwsza: 'Najpierw firma, potem klient',
} as const;
export type EsignOrder = keyof typeof ESIGN_ORDERS;

export const OTP_CHANNELS = { sms: 'SMS', email: 'E-mail' } as const;
export type OtpChannel = keyof typeof OTP_CHANNELS;

export const ESIGN_EVENT_LABELS: Record<string, string> = {
  utworzenie: 'Wysłano do podpisu',
  link_wyslany: 'Wysłano link do podpisu',
  link_ponowny: 'Wysłano nowy link (poprzedni unieważniony)',
  przypomnienie: 'Przypomnienie o podpisie w imieniu firmy',
  otwarcie: 'Otwarto dokument',
  kod_wyslany: 'Wysłano kod potwierdzenia',
  kod_bledny: 'Błędny kod potwierdzenia',
  podpis: 'Podpisano',
  odmowa: 'Odmowa podpisu',
  anulowanie: 'Anulowano podpisywanie',
  zmiana_podpisujacego: 'Zmieniono osobę podpisującą w imieniu firmy',
  finalizacja_blad: 'Błąd składania dokumentu (ponowienie automatyczne)',
  zakonczenie: 'Dokument podpisany i zapieczętowany',
};

/** Oświadczenia składane przy podpisie. Treść zapisywana dosłownie w śladzie dowodowym. */
export interface ConsentDef {
  code: string;
  text: string;
  required: boolean;
}

/** Oświadczenia przy zdalnym podpisie protokołu odbioru przez klienta. */
export function protocolConsents(code: string): ConsentDef[] {
  return [
    { code: 'tresc', text: `Zapoznałem(-am) się z treścią protokołu ${code} i potwierdzam odbiór opisanych w nim prac (z uwagami, jeśli wpisuję je poniżej).`, required: true },
    { code: 'forma', text: 'Zgadzam się na podpisanie protokołu w formie elektronicznej oraz na potwierdzenie mojej tożsamości jednorazowym kodem.', required: true },
  ];
}

export function consentsFor(role: 'klient' | 'firma', ctx: { number: string; companyName: string; consumer: boolean }): ConsentDef[] {
  if (role === 'firma') {
    return [
      { code: 'tresc', text: `Zapoznałem(-am) się z treścią dokumentu ${ctx.number} i akceptuję jego postanowienia.`, required: true },
      { code: 'reprezentacja', text: `Podpisuję w imieniu ${ctx.companyName} jako osoba upoważniona do jej reprezentowania.`, required: true },
    ];
  }
  const out: ConsentDef[] = [
    { code: 'tresc', text: `Zapoznałem(-am) się z treścią dokumentu ${ctx.number} i akceptuję jego postanowienia.`, required: true },
    {
      code: 'forma',
      text: 'Zgadzam się na zawarcie umowy w formie elektronicznej oraz na potwierdzenie mojej tożsamości jednorazowym kodem.',
      required: true,
    },
  ];
  if (ctx.consumer) {
    out.push(
      {
        code: 'odstapienie',
        text: 'Przyjmuję do wiadomości, że jako konsument mogę odstąpić od umowy zawartej na odległość w terminie 14 dni od jej zawarcia, bez podawania przyczyny.',
        required: true,
      },
      {
        code: 'wczesny_start',
        text: 'Żądam rozpoczęcia prac przed upływem terminu do odstąpienia od umowy. Wiem, że w razie odstąpienia zapłacę za prace wykonane do tego czasu.',
        required: false,
      },
    );
  }
  return out;
}

// ── walidacja ────────────────────────────────────────────────────────────

const str = (max: number) => z.string().trim().max(max);
const phone = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^(\+48)?\d{9}$/, 'Numer telefonu: 9 cyfr (opcjonalnie +48)'));

export const esignCreateSchema = z
  .object({
    client: z.object({
      name: str(200).min(3, 'Podaj imię i nazwisko podpisującego'),
      email: z.string().trim().toLowerCase().email('Podaj adres e-mail — na niego trafi link'),
      phone: z.union([phone, z.literal('')]).default(''),
      channel: z.enum(['sms', 'email']),
      consumer: z.boolean(),
    }),
    companySignerId: z.string().uuid('Wybierz osobę podpisującą w imieniu firmy'),
    order: z.enum(['klient_pierwszy', 'firma_pierwsza']).default('klient_pierwszy'),
    expiresDays: z.number().int().min(1).max(30).default(14),
    message: str(1000).default(''),
    /** QA P3: umowa wydana na papier — świadoma wysyłka do e-podpisu bez zmiany treści (zdanie o egzemplarzach, linie podpisu) */
    paperTextAccepted: z.boolean().default(false),
  })
  .refine((v) => v.client.channel !== 'sms' || v.client.phone !== '', { message: 'Kod SMS wymaga numeru telefonu', path: ['client', 'phone'] });

export const esignSignSchema = z.object({
  typedName: str(200).min(3, 'Wpisz imię i nazwisko'),
  consents: z.array(z.string().max(40)).max(10),
  code: z.string().trim().regex(/^\d{6}$/, 'Kod ma 6 cyfr'),
  /** protokół odbioru: uwagi klienta zgłoszone przy podpisie (umowa — pomijane) */
  remarks: z.string().trim().max(2000).default(''),
});

export const esignChangeSignerSchema = z.object({ userId: z.string().uuid() });

export const esignDeclineSchema = z.object({ reason: str(1000).min(3, 'Podaj powód') });

// ── DTO ──────────────────────────────────────────────────────────────────

export interface EsignSignerDto {
  id: string;
  seq: number;
  role: 'klient' | 'firma';
  name: string;
  email: string;
  phone: string;
  channel: OtpChannel;
  consumer: boolean;
  userId: string | null;
  status: EsignSignerStatus;
  signedAt: string | null;
  typedName: string;
  consents: { code: string; text: string; accepted: boolean }[];
  ip: string | null;
  declineReason: string;
  viewCount: number;
  /** kolej tego podpisującego */
  isNext: boolean;
}

export interface EsignEventDto {
  id: string;
  at: string;
  type: string;
  signerName: string | null;
  details: Record<string, unknown>;
  ip: string | null;
}

export interface EsignRequestDto {
  id: string;
  contractId: string;
  status: EsignStatus;
  order: EsignOrder;
  documentSha256: string;
  contentHash: string;
  expiresAt: string;
  createdAt: string;
  createdByName: string | null;
  completedAt: string | null;
  finalSha256: string | null;
  cancelReason: string;
  signers: EsignSignerDto[];
  events: EsignEventDto[];
  can: { cancel: boolean; resend: boolean; signAsCompany: boolean; changeCompanySigner: boolean };
}

export interface EsignCompanySignerDto {
  id: string;
  fullName: string;
  email: string;
}

/** Widok podpisującego (strona publiczna) — tylko dane tego dokumentu. */
export interface PublicEsignDto {
  demo: boolean;
  status: EsignStatus;
  documentKind: 'umowa' | 'aneks' | 'protokol';
  number: string;
  title: string;
  company: { fullName: string; address: string; nip: string; phone: string; email: string; color: string };
  /** skrót treści — ten sam co w stopce PDF */
  contentHash: string;
  /** skrót pliku PDF do podpisu */
  documentSha256: string;
  expiresAt: string;
  signer: {
    name: string;
    status: EsignSignerStatus;
    channel: OtpChannel;
    destinationMasked: string;
    signedAt: string | null;
    isNext: boolean;
  };
  consents: ConsentDef[];
  /** inne strony — bez danych kontaktowych */
  parties: { role: 'klient' | 'firma'; name: string; status: EsignSignerStatus; signedAt: string | null }[];
  finalAvailable: boolean;
  /** protokół odbioru: klient może dopisać uwagi przy podpisie; po podpisie — zgłoszone uwagi */
  remarks?: { allowed: boolean; text: string };
}
